// ===================================
// Payroll Preference Payload Builders
// ===================================

/**
 * Builds the array of configuration items when preference mode is "By Corporation".
 * Filters out completely empty rows and assigns 1-based RowOrder.
 *
 * @param {Array<Object>} corpRows - Array of corporation preference rows.
 * @returns {Array<Object>} Array of corporation detail payload objects.
 */
export const buildCorpDetailsPayload = (corpRows = []) => {
  return corpRows
    .filter((row) => row.corpId || row.automationType || row.payrollName)
    .map((row, index) => ({
      Id: row.detailId || "",
      CorporationId: row.corpId || "",
      PayrollVendorID: row.payrollName || "",
      DataAutomationType: Number(row.automationType) || 0,
      ApiKey: row.apiKey || "",
      SecurityKey: row.securityKey || "",
      PayrollEmail: row.apiOrEmail || "",
      ApiConfigured: Boolean(row.apiKey && row.securityKey),
      RowOrder: index + 1,
    }));
};

/**
 * Builds the single configuration item when preference mode is "For All Corporation".
 *
 * @param {Object} params
 * @param {string} params.allCorpDetailId - Stored detail ID for global preference.
 * @param {string} params.importGroup1 - Data automation type (e.g. Email vs API).
 * @param {string} params.importGroup2 - Payroll vendor ID.
 * @param {string|null} params.apiKey - Configured API Key.
 * @param {string|null} params.securityKey - Configured Security Key.
 * @returns {Array<Object>} Single-element array containing the global preference detail.
 */
export const buildAllCorpDetailsPayload = ({
  allCorpDetailId,
  importGroup1,
  importGroup2,
  apiKey,
  securityKey,
}) => {
  return [
    {
      Id: allCorpDetailId || "",
      PayrollVendorID: importGroup2 || "",
      DataAutomationType: Number(importGroup1) || 0,
      ApiKey: apiKey || "",
      SecurityKey: securityKey || "",
      ApiConfigured: Boolean(apiKey && securityKey),
      RowOrder: 1,
    },
  ];
};

/**
 * Assembles the full backend save payload for payroll preferences.
 *
 * @param {Object} params
 * @param {string} params.preferenceId - Main preference record ID.
 * @param {boolean} params.isPayrollHours - Whether hours are converted to minutes or decimals.
 * @param {string} params.companyPreference - Preference mode ('by_corporation' or 'all_corporation').
 * @param {string} params.byCorpModeKey - Constant key representing by-corporation mode.
 * @param {Array<Object>} params.corpRows - Current rows in by-corporation table.
 * @param {string} params.allCorpDetailId - Detail ID for all-corporation mode.
 * @param {string} params.importGroup1 - Automation type for all-corporation mode.
 * @param {string} params.importGroup2 - Vendor ID for all-corporation mode.
 * @param {string|null} params.apiKey - API Key for all-corporation mode.
 * @param {string|null} params.securityKey - Security Key for all-corporation mode.
 * @returns {Object} Complete save payload for savePayrollPreference API.
 */
export const buildPayrollPreferencePayload = ({
  preferenceId,
  isPayrollHours,
  companyPreference,
  byCorpModeKey,
  corpRows,
  allCorpDetailId,
  importGroup1,
  importGroup2,
  apiKey,
  securityKey,
}) => {
  const isByCorp = companyPreference === byCorpModeKey;

  const details = isByCorp
    ? buildCorpDetailsPayload(corpRows)
    : buildAllCorpDetailsPayload({
        allCorpDetailId,
        importGroup1,
        importGroup2,
        apiKey,
        securityKey,
      });

  return {
    Id: preferenceId || "",
    IsPayrollHours: isPayrollHours,
    IsByCorporation: isByCorp,
    Details: details,
  };
};
